import { compareLocalDate, nationalityName } from '@felix/domain';
import type { Content, CustomTableLayout, TDocumentDefinitions } from 'pdfmake/interfaces.js';
import { formatDay } from '../../format.js';
import { TOKENS } from '../../tokens.js';
import { safeFileBase } from '../drills/report-pdf.js';
import {
  pdfFacts,
  pdfHeading,
  pdfText,
  type RecordPdfT,
  recordPdf,
} from '../reports/record-pdf.js';
import type { ReportBrand } from '../voyage/source.js';
import { portText } from './crew-profile-pdf.js';
import type { CrewAssignment, CrewCertificates, CrewProfileView } from './types.js';

/**
 * Record of Employment (ADR 0131 §9 Faz 3, Ek 3): gemiadamının gemideki hizmetinin kaydı
 * [MLC 2006 Standard A2.1 §1(e); Guideline B2.1.1]. Yazılanlar: kişi (ad, Crew ID, doğum tarihi ve
 * yeri, uyruk, kimlik belgesi), gemi (ad, IMO, bayrak), görevi (rütbe), katılış ve ayrılış tarihi
 * ve limanı.
 *
 * **Yazılmayanlar** [MLC 2006 Standard A2.1 §3: "shall not contain any statement as to the quality
 * of the seafarers' work or as to their wages"]: ücret, işin değerlendirmesi, ayrılış nedeni ve
 * notu (neden "disciplinary" olabilir; değerlendirme sayılır), kontrat bilgisi.
 *
 * İmza kutuları (kaptan, şirket temsilcisi) boştur; elle imzalanır. Yalnız ayrılışı gerçekleşmiş
 * (Completed) atama için verilir. Türkçe arayüzde başlık ve etiketler iki dillidir (İngilizce /
 * Türkçe); belge yabancı limanda da okunur.
 */

export interface EmploymentRecordInput {
  view: CrewProfileView;
  assignment: CrewAssignment;
  /** Kimlik belgesi numarası için; okunamadıysa `null` (alan boş). */
  certificates: CrewCertificates | null;
  brand: ReportBrand | null;
  printedAt: string;
  locale: string;
}

/** Ayrılışı gerçekleşmiş atama: `valid_to ≤ bugün` (ADR 0131 §3 "Completed"). */
export function canIssueEmploymentRecord(
  assignment: Pick<CrewAssignment, 'validTo'>,
  today: string,
): boolean {
  return assignment.validTo !== null && compareLocalDate(assignment.validTo, today) <= 0;
}

/**
 * Kimlik belgesi: geçerli Seaman's Book (gemiadamı kimlik belgesi; ILO C108/C185), yoksa geçerli
 * pasaport; geçerlisi yoksa süresi geçmiş olanlar aynı sırayla; hiçbiri yoksa `null`.
 */
export function employmentDocument(
  certificates: CrewCertificates | null,
): { code: 'SBOOK' | 'PASSPORT'; number: string } | null {
  if (!certificates) return null;
  const rows = certificates.certificates.filter(
    (c) => (c.code === 'SBOOK' || c.code === 'PASSPORT') && c.number,
  );
  const rank = (c: (typeof rows)[number]) =>
    (c.status === 'expired' ? 2 : 0) + (c.code === 'SBOOK' ? 0 : 1);
  const best = [...rows].sort((a, b) => rank(a) - rank(b))[0];
  return best?.number ? { code: best.code as 'SBOOK' | 'PASSPORT', number: best.number } : null;
}

const day = (value: string | null | undefined) => (value ? formatDay(value) : '—');

const BOX: CustomTableLayout = {
  hLineWidth: () => 0.6,
  vLineWidth: () => 0.6,
  hLineColor: () => TOKENS.border,
  vLineColor: () => TOKENS.border,
  paddingLeft: () => 6,
  paddingRight: () => 6,
  paddingTop: () => 4,
  paddingBottom: () => 4,
};

export function employmentRecordPdf(
  t: RecordPdfT,
  input: EmploymentRecordInput,
): TDocumentDefinitions {
  const e = (key: string, options?: Record<string, unknown>) =>
    t(`crewProfile.employment.${key}`, options);
  const f = (key: string) => e(`fields.${key}`);
  const { view, assignment: a } = input;
  const profile = view.profile;
  const document = employmentDocument(input.certificates);
  const content: Content[] = [
    { text: e('subtitle'), style: 'muted', margin: [0, 0, 0, 2] },
    pdfHeading(e('seafarer')),
    pdfFacts([
      [f('fullName'), view.fullName],
      [f('crewCode'), pdfText(profile?.crewCode)],
      [f('dateOfBirth'), day(profile?.dateOfBirth)],
      [f('placeOfBirth'), pdfText(profile?.placeOfBirth)],
      [f('nationality'), pdfText(nationalityName(profile?.nationality, input.locale))],
      [f('document'), document ? `${e(`docType.${document.code}`)} ${document.number}` : '—'],
    ]),
    pdfHeading(e('ship')),
    pdfFacts([
      [f('vesselName'), a.vesselName],
      [f('imo'), pdfText(a.vesselImo)],
      [f('flag'), pdfText(a.vesselFlag)],
    ]),
    pdfHeading(e('service')),
    pdfFacts([
      [f('rank'), pdfText(a.rank)],
      ['', ''],
      [f('joined'), day(a.validFrom)],
      [f('joinPort'), portText(a.joinPort, a.joinPortCode)],
      [f('signedOff'), day(a.validTo)],
      [f('signOffPort'), portText(a.signOffPort, a.signOffPortCode)],
    ]),
    { text: e('statement'), style: 'muted', margin: [0, 8, 0, 0] },
    {
      unbreakable: true,
      table: {
        widths: ['*', '*'],
        heights: [70],
        body: [
          [
            { text: e('signatureMaster'), fontSize: 8 },
            { text: e('signatureCompany'), fontSize: 8 },
          ],
        ],
      },
      layout: BOX,
      margin: [0, 14, 0, 0],
    },
  ];
  return recordPdf(t, {
    brand: input.brand,
    title: e('title'),
    number: [profile?.crewCode, view.fullName].filter(Boolean).join(' · '),
    marks: [],
    subject: `${view.fullName} · ${a.vesselName}`,
    content,
    approvals: null,
    printedAt: input.printedAt,
  });
}

/** Dosya adı: "record-of-employment-C-045-Gizem-Koc-M-Y-BGM-03-2026-09-27.pdf". */
export function employmentRecordFileName(
  view: Pick<CrewProfileView, 'fullName' | 'profile'>,
  assignment: Pick<CrewAssignment, 'vesselName' | 'validTo'>,
): string {
  const parts = [
    'record-of-employment',
    view.profile?.crewCode ?? '',
    view.fullName,
    assignment.vesselName,
    assignment.validTo ?? '',
  ];
  return `${safeFileBase(parts.filter(Boolean).join('-'))}.pdf`;
}
